// The form's entire state: values keyed by attribute name, errors, JSON-editor
// toggles, validation and the save payload. Every update is immutable, so no
// field handler ever clones the whole attribute tree.

import { useCallback, useMemo, useReducer } from 'react';

import { isOutputPayloadField } from '../core/fieldTypes';
import * as headersModel from '../core/headers';
import { labelFor, requiredNames } from '../core/schema';
import {
  ERROR_CODES,
  isFormConfigured,
  validateEmailList,
  validateExtension,
} from '../core/validation';
import {
  attributesToValues,
  normalizeTextValue,
  serializeValue,
} from '../core/values';

const isValidJson = (value) => {
  try {
    JSON.parse(value);
    return true;
  } catch (e) {
    return false;
  }
};

const SET_VALUE = 'SET_VALUE';
const SET_VALUES = 'SET_VALUES';
const SET_ERROR = 'SET_ERROR';
const SET_JSON_TOGGLE = 'SET_JSON_TOGGLE';
const REPLACE_JSON_TOGGLES = 'REPLACE_JSON_TOGGLES';
const REMOVE_HEADER_ENTRY = 'REMOVE_HEADER_ENTRY';
const RESET = 'RESET';

const reducer = (state, action) => {
  switch (action.type) {
    case SET_VALUE:
      return {
        ...state,
        values: { ...state.values, [action.name]: action.value },
      };
    case SET_VALUES:
      return { ...state, values: action.updater(state.values) };
    case SET_ERROR:
      return {
        ...state,
        errors: { ...state.errors, [action.name]: action.error },
      };
    case SET_JSON_TOGGLE:
      return {
        ...state,
        jsonEditorOn: { ...state.jsonEditorOn, [action.name]: action.on },
        jsonEditorDisabled: {
          ...state.jsonEditorDisabled,
          [action.name]: action.disabled,
        },
      };
    case REPLACE_JSON_TOGGLES:
      return {
        ...state,
        jsonEditorOn: action.on,
        jsonEditorDisabled: action.disabled,
      };
    case REMOVE_HEADER_ENTRY: {
      const remaining = headersModel.removeEntry(
        state.values[action.name],
        action.index,
      );

      return {
        ...state,
        values: { ...state.values, [action.name]: remaining },
        errors: headersModel.reindexEntryErrors(
          state.errors,
          action.index,
          remaining.length,
        ),
      };
    }
    case RESET:
      return {
        values: action.values,
        errors: {},
        jsonEditorOn: {},
        jsonEditorDisabled: {},
      };
    default:
      return state;
  }
};

const initialState = (values) => ({
  values,
  errors: {},
  jsonEditorOn: {},
  jsonEditorDisabled: {},
});

// `schema` is the grouped schema ({ commandName: [descriptor] }) and `values` is
// keyed by attribute name — never by array position, which is what forced the
// original screens into findIndex on every keystroke.
export default function useDynamicForm({
  schema = {},
  initialAttributes = [],
  // How a field name becomes its label, and how a checkbox state is persisted.
  // Both differ between consumers, so both are injectable.
  labelFor: customLabelFor,
  checkboxValue = (checked) => checked.toString(),
} = {}) {
  const [state, dispatch] = useReducer(
    reducer,
    attributesToValues(initialAttributes),
    initialState,
  );

  const { values, errors, jsonEditorOn, jsonEditorDisabled } = state;

  const descriptors = useMemo(() => Object.values(schema).flat(), [schema]);

  const attributeNames = useMemo(
    () => descriptors.map((descriptor) => descriptor.name),
    [descriptors],
  );

  const required = useMemo(() => requiredNames(schema), [schema]);

  const fields = useMemo(
    () => Object.keys(values).map((name) => ({ name, value: values[name] })),
    [values],
  );

  const labelOf = useCallback(
    (name) =>
      customLabelFor ? customLabelFor(name) : labelFor(name, attributeNames),
    [attributeNames, customLabelFor],
  );

  const descriptorOf = useCallback(
    (name) => descriptors.find((descriptor) => descriptor.name === name),
    [descriptors],
  );

  const fileTypeFields = useMemo(
    () =>
      fields
        .filter((field) => required.includes(field.name))
        .filter((field) => labelOf(field.name) === 'file_type'),
    [fields, required, labelOf],
  );

  const setError = useCallback((name, error) => {
    dispatch({ type: SET_ERROR, name, error });
  }, []);

  const syncJsonToggle = useCallback((name, value) => {
    if (!isOutputPayloadField(name)) return;

    const parsable = isValidJson(value);

    dispatch({
      type: SET_JSON_TOGGLE,
      name,
      on: parsable,
      disabled: !parsable,
    });
  }, []);

  const setValue = useCallback(
    (name, rawValue) => {
      const value = normalizeTextValue(rawValue);

      dispatch({ type: SET_VALUE, name, value });
      syncJsonToggle(name, rawValue);
    },
    [syncJsonToggle],
  );

  const setValues = useCallback((updater) => {
    dispatch({ type: SET_VALUES, updater });
  }, []);

  const setCheckbox = useCallback(
    (name, checked) => {
      dispatch({ type: SET_VALUE, name, value: checkboxValue(checked) });
    },
    [checkboxValue],
  );

  const setJsonData = useCallback((name, data) => {
    dispatch({ type: SET_VALUE, name, value: JSON.stringify(data) });
  }, []);

  const toggleJsonEditor = useCallback(
    (name, on) => {
      const parsable = isValidJson(values[name]);

      dispatch({
        type: SET_JSON_TOGGLE,
        name,
        on: on && parsable,
        disabled: !parsable,
      });
    },
    [values],
  );

  const disableJsonEditor = useCallback((name) => {
    dispatch({ type: SET_JSON_TOGGLE, name, on: false, disabled: true });
  }, []);

  // Seeded from the stored attributes rather than from live form state, so that
  // re-running it does not fight setValue's own toggle sync.
  const initJsonToggles = useCallback(
    (attributes) => {
      const stored = attributesToValues(attributes ?? []);
      const on = {};
      const disabled = {};

      descriptors
        .filter((descriptor) => isOutputPayloadField(descriptor.name))
        .forEach((descriptor) => {
          const parsable = isValidJson(stored[descriptor.name] ?? '');
          on[descriptor.name] = parsable;
          disabled[descriptor.name] = !parsable;
        });

      dispatch({ type: REPLACE_JSON_TOGGLES, on, disabled });
    },
    [descriptors],
  );

  const headers = useMemo(
    () => ({
      add: (name) =>
        dispatch({
          type: SET_VALUE,
          name,
          value: headersModel.addEntry(values[name]),
        }),
      remove: (name, index) =>
        dispatch({ type: REMOVE_HEADER_ENTRY, name, index }),
      change: (name, change) =>
        dispatch({
          type: SET_VALUE,
          name,
          value: headersModel.changeEntry(values[name], change),
        }),
    }),
    [values],
  );

  // Validates an explicit value rather than reading state, because the field
  // handlers validate in the same tick as setValue — reading `values` there
  // would still hold the previous keystroke.
  //
  // The "invalid extension" message always wins over anything validateExtension
  // itself would have reported, mirroring the original validateField.
  const validateValue = useCallback(
    (name, value) => {
      const descriptor = descriptorOf(name);

      if (descriptor?.required && String(value ?? '').trim() === '') {
        setError(name, { code: ERROR_CODES.BLANK, params: {} });
        return false;
      }

      const { valid } = validateExtension({
        field: { name, value },
        label: labelOf(name),
        fileTypeFields,
      });

      setError(
        name,
        valid ? null : { code: ERROR_CODES.INVALID_EXTENSION, params: {} },
      );

      return valid;
    },
    [descriptorOf, labelOf, fileTypeFields, setError],
  );

  const blur = useCallback(
    (name) => validateValue(name, values[name]),
    [validateValue, values],
  );

  const validateEmailValue = useCallback(
    (name, value) => {
      const descriptor = descriptorOf(name);

      if (descriptor?.required && String(value ?? '').trim() === '') {
        setError(name, { code: ERROR_CODES.BLANK, params: {} });
        return false;
      }

      const { valid, error } = validateEmailList(value);
      setError(name, error);
      return valid;
    },
    [descriptorOf, setError],
  );

  const blurEmail = useCallback(
    (name) => validateEmailValue(name, values[name]),
    [validateEmailValue, values],
  );

  const setHeaderError = useCallback(
    (inputName, blank) => {
      setError(
        inputName,
        blank ? { code: ERROR_CODES.BLANK, params: {} } : null,
      );
    },
    [setError],
  );

  const reset = useCallback((attributes) => {
    dispatch({ type: RESET, values: attributesToValues(attributes) });
  }, []);

  const isConfigured = useMemo(
    () => isFormConfigured(fields, required),
    [fields, required],
  );

  // Required fields additionally have to survive the extension / recipients
  // checks before the form can be submitted.
  const valuesValid = useMemo(
    () =>
      fields
        .filter((field) => required.includes(field.name))
        .every(
          (field) =>
            validateExtension({
              field,
              label: labelOf(field.name),
              fileTypeFields,
            }).valid,
        ),
    [fields, required, labelOf, fileTypeFields],
  );

  const buildAttributes = useCallback(
    () =>
      headersModel.stripEditedFlag(
        Object.keys(values).map((name) => ({
          name,
          value: serializeValue(values[name]),
        })),
      ),
    [values],
  );

  return {
    values,
    errors,
    jsonEditorOn,
    jsonEditorDisabled,
    isConfigured,
    valuesValid,
    labelOf,
    descriptorOf,
    fileTypeFields,
    setValue,
    setValues,
    setCheckbox,
    setJsonData,
    setError,
    setHeaderError,
    toggleJsonEditor,
    disableJsonEditor,
    initJsonToggles,
    headers,
    blur,
    blurEmail,
    validateValue,
    validateEmailValue,
    reset,
    buildAttributes,
  };
}
